// Same-origin REST client (no CORS): every call goes to /api on the dashboard's own host.

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

function messageFrom(body: any, fallback: string): string {
  const d = body?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d.map((e: any) => e.msg).join("; ");
  return fallback;
}

export async function request<T>(method: string, path: string, body?: unknown, opts: { timeoutMs?: number; form?: FormData } = {}): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), opts.timeoutMs ?? 30000);
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") headers["X-ADR-CSRF"] = "1";
  let payload: BodyInit | undefined;
  if (opts.form) payload = opts.form;
  else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: payload, credentials: "same-origin", signal: controller.signal });
  } catch (e: any) {
    throw new ApiError(0, e?.name === "AbortError" ? "The server did not respond in time." : "Cannot reach the server. Check your connection.");
  } finally {
    window.clearTimeout(timer);
  }
  const text = await res.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/api/auth/")) unauthorizedListeners.forEach((fn) => fn());
    const fallback =
      res.status >= 500 || typeof data === "string"
        ? `Server error (${res.status}). The API may be restarting; try again shortly.`
        : `Request failed (${res.status})`;
    throw new ApiError(res.status, messageFrom(data, fallback), data?.detail);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, timeoutMs?: number) => request<T>("GET", path, undefined, { timeoutMs }),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  del: <T>(path: string) => request<T>("DELETE", path),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<T>("POST", path, undefined, { form, timeoutMs: 120000 });
  },
};

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : "";
}
